import React, { FC, useState } from "react";
import { Container, Grid, makeStyles, Theme, Box, GridProps, BoxProps, CircularProgress } from '@material-ui/core';
import { SubmitButton, U1TextField } from 'components/atoms';
import { Field, Form } from 'react-final-form';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import { isPhoneInvalid, parsePhoneOnType } from 'utils/phoneHelpers';
import { EmailIcon } from 'svgs';
import { useFormSender } from 'hooks';
import U1CheckBox from "../../components/atoms/U1CheckBox";

const useStyles = makeStyles((theme: Theme) => ({
  message: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(2),
    },
    '& div': {
      maxHeight: 130,
      [theme.breakpoints.up('sm')]: {
        minHeight: 205,
      },
      '& div': {
        '& textarea': {
          minHeight: 130,
          paddingTop: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            minHeight: 205,
          },
          '&:focus': {
            paddingTop: theme.spacing(2),
            maxHeight: 130,
            height: '130px !important',
            [theme.breakpoints.up('sm')]: {
              height: '205px !important',
            },
          },
        },
      },
    },
  },
  grid: {
    padding: 0,
  },
  spinner: {
    color: theme.palette.common.white,
  },
}));

const validator = ({ email = '', firstName = '', lastName = '', phone = '' }) => {
  const errors: Record<string, string> = {};

  // Email
  if (!isEmail(email)) errors.email = 'Invalid Email address';
  if (isEmpty(email)) errors.email = 'Required field';

  // First Name
  if (isEmpty(firstName)) errors.firstName = 'Required field';

  // Last Name
  if (isEmpty(lastName)) errors.lastName = 'Required field';

  // Subject
  if (isPhoneInvalid(phone)) errors.phone = 'Invalid phone number';

  return errors;
};
const ContactForm: FC = () => {
  const {
    onSubmit,
    status: { error, loading, success },
  } = useFormSender('/api/contact-us');
  const isSubmitted = success || loading;
  const classes = useStyles();
  const fields: Array<{
    label: string;
    name: string;
    additionalFieldParams?: unknown;
    Icon?: unknown;
    multiline?: unknown;
    gridParams?: GridProps;
    wrapperParams?: BoxProps;
  }> = [
    {
      label: 'First Name*',
      name: 'firstName',
      gridParams: {
        md: 6,
        xs: 12,
      },
    },
    {
      label: 'Last Name*',
      name: 'lastName',
      gridParams: {
        md: 6,
        xs: 12,
      },
    },
    {
      label: 'Email*',
      name: 'email',
      gridParams: {
        md: 6,
        xs: 12,
      },
      Icon: {
        startIcon: <EmailIcon />,
        hasIcon: true,
      },
    },
    {
      label: 'Phone Number',
      name: 'phone',
      gridParams: {
        md: 6,
        xs: 12,
      },
      additionalFieldParams: {
        parse: parsePhoneOnType,
      },
    },
    {
      label: 'Message',
      name: 'message',
      gridParams: {
        xs: 12,
      },
      wrapperParams: {
        className: classes.message,
      },
      multiline: {
        multiline: true,
      },
    },
  ];

  const [check, setCheck] = useState<boolean>(true);

  const handleSubmit = async (values, actions) => {
    await onSubmit(values, actions, undefined, {
      agreeToReceive: check ? 'yes' : 'no',
    });
  };

  return (
    <Form
      validate={validator}
      onSubmit={handleSubmit}
      render={({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Container fixed className={classes.grid}>
            <Grid container spacing={5}>
              {fields.map(({ label, name, additionalFieldParams, Icon, multiline, wrapperParams, gridParams }, index) => (
                <Grid item {...gridParams} key={index}>
                  <Field name={name} {...additionalFieldParams}>
                    {({ input, meta: { error, touched } }) => (
                      <Box {...wrapperParams}>
                        <U1TextField
                          {...Icon}
                          {...multiline}
                          {...input}
                          disabled={isSubmitted}
                          label={label}
                          helperText={touched && error}
                          error={touched && !!error}
                        />
                      </Box>
                    )}
                  </Field>
                </Grid>
              ))}
            </Grid>
            <Box mt={4} />
            <Box>
              <U1CheckBox
                text={'By submitting this form you agree to receive text messages'}
                onChange={() => setCheck(!check)}
                checked={check}
              />
            </Box>
          </Container>
          <Box mt={6} />
          <SubmitButton disabled={submitting} error={error} success={success} fullWidth>
            {loading && <CircularProgress size="30px" className={classes.spinner} />}
            {success && 'Thank you for filling out your information!'}
            {error && 'An error occurred. Please try again.'}
            {!loading && !success && !error && 'Send'}
          </SubmitButton>
        </form>
      )}
    />
  );
};

export default ContactForm;
